/*
  Spectre core: loaded on every page as a module, before the other theme modules.
  Exposes window.Spectre for the other modules (no build step, no import maps,
  so modules share helpers through this namespace instead of import statements).
*/
const Spectre = (window.Spectre = window.Spectre || {});
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Event bus ---------- */
const bus = new EventTarget();
Spectre.on = (type, handler) => {
  const listener = (event) => handler(event.detail);
  bus.addEventListener(type, listener);
  return () => bus.removeEventListener(type, listener);
};
Spectre.emit = (type, detail = {}) => bus.dispatchEvent(new CustomEvent(type, { detail }));

/* ---------- Screen reader announcements ---------- */
Spectre.announce = (message) => {
  const region = document.getElementById('SpectreLive');
  if (!region || !message) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
};

/* ---------- Section Rendering API ---------- */
Spectre.fetchSections = async (ids, url = window.location.pathname + window.location.search, signal) => {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('sections', [].concat(ids).join(','));
  const response = await fetch(target.toString(), { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Sections request failed: ${response.status}`);
  return response.json();
};

Spectre.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

/* Replace the inner HTML of every [attribute] region in `root` with the region of the same name in `source`. */
Spectre.swapRegions = (root, source, attribute = 'data-region') => {
  root.querySelectorAll(`[${attribute}]`).forEach((region) => {
    const name = region.getAttribute(attribute);
    const next = source.querySelector(`[${attribute}="${CSS.escape(name)}"]`);
    if (next && next.innerHTML !== region.innerHTML) region.innerHTML = next.innerHTML;
  });
};

/* ---------- Money ---------- */
Spectre.formatMoney = (cents) => {
  const format = Spectre.moneyFormat || '${{amount}}';
  const value = (Number(cents) || 0) / 100;
  const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
    const [whole, fraction] = number.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(value, 0);
      case 'amount_with_comma_separator':
        return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(value, 0, '.', ',');
      default:
        return withDelimiters(value, 2);
    }
  });
};

/* ---------- Cart ---------- */
const cart = (Spectre.cart = {
  /* Sections the cart drawer asks to refresh with every change (set by the drawer when it is on the page). */
  sections() {
    return typeof Spectre.cartSections === 'function' ? Spectre.cartSections() : [];
  },

  async request(url, body) {
    const sections = cart.sections();
    const init = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
    if (body instanceof FormData) {
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }
      init.body = body;
    } else {
      const payload = { ...body };
      if (sections.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(payload);
    }
    const response = await fetch(url, init);
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || Spectre.strings?.addError);
      error.description = data.description || data.message || '';
      error.status = response.status;
      throw error;
    }
    return data;
  },

  /* body: FormData from a product form, or { items: [{ id, quantity, properties }] } */
  async add(body, source = 'add') {
    try {
      const data = await cart.request(`${Spectre.routes.cartAdd}.js`, body);
      Spectre.emit('cart:added', { item: data, sections: data.sections || null, source });
      return data;
    } catch (error) {
      /* Asking for more than is in stock: Shopify adds what it can, then reports it. Show the real cart. */
      if (error.status === 422) {
        Spectre.emit('cart:changed', { cart: null, sections: null, source });
        if (!cart.sections().length) cart.get().then((state) => cart.setCount(state.item_count)).catch(() => {});
      }
      throw error;
    }
  },

  /* Change a line by its key; quantity 0 removes it */
  async change(key, quantity, source = 'cart') {
    const data = await cart.request(`${Spectre.routes.cartChange}.js`, { id: key, quantity });
    Spectre.emit('cart:changed', { cart: data, sections: data.sections || null, source });
    return data;
  },

  async get() {
    const response = await fetch(`${Spectre.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  },

  setCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
    const labels = Spectre.strings?.cartCount;
    if (!labels) return;
    document.querySelectorAll('[data-cart-count-label]').forEach((element) => {
      element.textContent = (count === 1 ? labels.one : labels.other).replace('[count]', count);
    });
  },
});

/* Without a cart drawer, keep the header count right after an add. */
Spectre.on('cart:added', async () => {
  if (cart.sections().length) return;
  try {
    const state = await cart.get();
    cart.setCount(state.item_count);
  } catch (error) {
    /* The count refreshes on the next page */
  }
});

/* One-tap add buttons: data-quick-add="<variant id>" (Complete the stack, cart upsell) */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-quick-add]');
  if (!button || button.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();
  const body = new FormData();
  body.append('id', button.dataset.quickAdd);
  body.append('quantity', '1');
  button.setAttribute('aria-busy', 'true');
  const label = button.querySelector('span');
  const previous = label?.textContent;
  try {
    await cart.add(body, button.dataset.quickAddSource || 'quick-add');
    Spectre.announce(Spectre.strings?.added);
    if (label && document.contains(label)) {
      label.textContent = Spectre.strings?.addedShort || previous;
      window.setTimeout(() => {
        label.textContent = previous;
      }, 2400);
    }
  } catch (error) {
    Spectre.announce(error.description || Spectre.strings?.addError);
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/* ---------- WhatsApp ---------- */
const whatsapp = (Spectre.whatsapp = {
  number() {
    return String(Spectre.settings?.whatsappNumber || '').replace(/[^\d]/g, '');
  },

  /* Fill a message template. A line whose token resolves to an empty value is dropped. */
  build(template, tokens = {}) {
    return String(template || '')
      .split(/\r?\n/)
      .map((line) => {
        let empty = false;
        const filled = line.replace(/\[(\w+)\]/g, (match, key) => {
          if (!(key in tokens)) return match;
          const value = tokens[key] == null ? '' : String(tokens[key]).trim();
          if (!value) empty = true;
          return value;
        });
        return empty ? null : filled.trim();
      })
      .filter((line) => line !== null && line !== '')
      .join('\n');
  },

  url(message) {
    const number = whatsapp.number();
    return `https://wa.me/${number}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
  },

  track(source, detail = {}) {
    if (!Spectre.settings?.whatsappTracking) return;
    try {
      window.Shopify?.analytics?.publish?.('spectre_whatsapp_click', {
        source,
        page: window.location.pathname,
        ...detail,
      });
    } catch (error) {
      /* Analytics must never block the WhatsApp link */
    }
  },
});

/*
  <whatsapp-link>: wraps an <a> whose href Liquid already filled in (works without JavaScript).
  data-template: message template; data-tokens: JSON tokens; data-source: pdp, cart, float, footer...
  Other scripts update tokens with element.setTokens({...}).
*/
class WhatsAppLink extends HTMLElement {
  connectedCallback() {
    this.anchor = this.querySelector('a');
    if (!this.anchor) return;
    try {
      this.tokens = JSON.parse(this.dataset.tokens || '{}');
    } catch (error) {
      this.tokens = {};
    }
    this.anchor.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.anchor?.removeEventListener('click', this.onClick);
  }

  setTokens(tokens) {
    this.tokens = { ...this.tokens, ...tokens };
    this.render();
  }

  render() {
    if (!this.anchor || !this.dataset.template) return;
    this.anchor.href = whatsapp.url(whatsapp.build(this.dataset.template, this.tokens));
  }

  onClick = () => {
    whatsapp.track(this.dataset.source || 'link', {
      product: this.tokens.product || null,
      variant: this.tokens.variant || null,
      variant_id: this.dataset.variantId || null,
    });
  };
}
if (!customElements.get('whatsapp-link')) customElements.define('whatsapp-link', WhatsAppLink);

/* ---------- Drawers and modals (native <dialog>) ---------- */
/*
  <spectre-drawer id="..."><dialog class="drawer">...</dialog></spectre-drawer>
  Open with any element carrying data-drawer-open="<id>" (or call element.open(trigger)).
  showModal() keeps focus inside and makes the page behind inert; focus returns to the trigger.
*/
class SpectreDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('click', this.onBackdropClick);
    this.dialog.addEventListener('close', this.onClosed);
    this.querySelectorAll('[data-drawer-close]').forEach((button) => button.addEventListener('click', () => this.close()));
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  open(trigger) {
    if (!this.dialog || this.isOpen) return;
    this.trigger = trigger || document.activeElement;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    document.body.classList.add('has-modal');
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    this.trigger?.setAttribute?.('aria-expanded', 'true');
    const focusTarget = this.dialog.querySelector('[autofocus], [data-drawer-focus]');
    focusTarget?.focus();
    Spectre.emit('drawer:open', { id: this.id });
  }

  close() {
    if (!this.isOpen) return;
    this.dialog.classList.remove('is-open');
    const finish = () => {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.onClosed();
      }
    };
    if (reducedMotion.matches) finish();
    else window.setTimeout(finish, 280);
  }

  onClosed = () => {
    this.trigger?.setAttribute?.('aria-expanded', 'false');
    /* Another drawer can already be open (quick add hands over to the cart): leave it in charge */
    const otherOpen = document.querySelector('dialog[open]');
    if (!otherOpen) {
      document.body.classList.remove('has-modal');
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
    }
    Spectre.emit('drawer:close', { id: this.id });
  };

  onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  onBackdropClick = (event) => {
    if (event.target === this.dialog) this.close();
  };
}
if (!customElements.get('spectre-drawer')) customElements.define('spectre-drawer', SpectreDrawer);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
  if (!drawer || typeof drawer.open !== 'function') return;
  event.preventDefault();
  drawer.open(trigger);
});

/* ---------- Ghost-in: below-the-fold headings and images resolve from a soft blur, once ---------- */
const ghost = () => {
  if (!Spectre.settings?.motionGhost || reducedMotion.matches || !('IntersectionObserver' in window)) return;
  const seen = new WeakSet();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const element = entry.target;
        if (!seen.has(element)) {
          seen.add(element);
          if (entry.isIntersecting) {
            observer.unobserve(element);
            return;
          }
          element.classList.add('ghost-wait');
          return;
        }
        if (entry.isIntersecting) {
          element.classList.remove('ghost-wait');
          element.classList.add('ghost-in');
          observer.unobserve(element);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  const observe = (root = document) => root.querySelectorAll('[data-ghost]').forEach((element) => observer.observe(element));
  observe();
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
};

/* ---------- Pause animations that are off screen (hero light sweep) ---------- */
const pauseOffscreen = () => {
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.classList.toggle('is-offscreen', !entry.isIntersecting));
  });
  const observe = (root = document) => root.querySelectorAll('[data-pause-offscreen]').forEach((element) => observer.observe(element));
  observe();
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
};

/* ---------- Card to product morph (cross-document View Transitions, progressive) ---------- */
const viewTransitions = () => {
  if (!Spectre.settings?.motionViewTransitions || reducedMotion.matches) return;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-vt-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    document.querySelectorAll('[data-vt-active]').forEach((element) => {
      element.style.viewTransitionName = '';
      element.removeAttribute('data-vt-active');
    });
    const media = link.closest('[data-vt-card]')?.querySelector('[data-vt-media]');
    if (!media) return;
    media.style.viewTransitionName = 'product-media';
    media.setAttribute('data-vt-active', '');
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-vt-active]').forEach((element) => {
      element.style.viewTransitionName = '';
      element.removeAttribute('data-vt-active');
    });
  });
};

/* ---------- Card photo swipe (touch screens): a sideways swipe shows the second photo ---------- */
const cardSwipe = () => {
  let start = null;
  let swiped = null;
  document.addEventListener(
    'touchstart',
    (event) => {
      swiped = null;
      const media = event.target.closest?.('.card__media--hover');
      const touch = event.touches[0];
      start = media && event.touches.length === 1 ? { media, x: touch.clientX, y: touch.clientY } : null;
    },
    { passive: true }
  );
  document.addEventListener(
    'touchend',
    (event) => {
      if (!start) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        const rtl = document.documentElement.dir === 'rtl';
        start.media.classList.toggle('is-second', dx < 0 !== rtl);
        swiped = start.media;
      }
      start = null;
    },
    { passive: true }
  );
  /* A swipe never opens the product */
  document.addEventListener(
    'click',
    (event) => {
      if (swiped && event.target.closest?.('.card__media--hover') === swiped) {
        event.preventDefault();
        event.stopPropagation();
      }
      swiped = null;
    },
    true
  );
};

/* ---------- WhatsApp float yields to on-screen WhatsApp buttons and sticky bars ---------- */
const floatYield = () => {
  const float = document.querySelector('[data-wa-float]');
  if (!float || !('IntersectionObserver' in window)) return;
  const visible = new Set();
  const update = () => float.classList.toggle('is-yielding', visible.size > 0 || document.body.classList.contains('has-sticky-cta'));
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target)));
    update();
  });
  const observe = (root = document) => root.querySelectorAll('[data-wa-yield]').forEach((element) => observer.observe(element));
  observe();
  Spectre.on('sticky:change', update);
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
};

/* ---------- Theme editor: the block being edited is shown ---------- */
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const details = block.matches('details') ? block : block.closest('details');
    if (details) details.open = true;
    const bar = block.closest('announcement-bar');
    if (bar?.items && typeof bar.show === 'function') {
      bar.stop?.();
      bar.show(bar.items.indexOf(block.closest('.announcement__item') || block));
    }
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.closest('announcement-bar')?.start?.();
  });
}

/* ---------- Share buttons (articles): the phone's share sheet, or copy the link ---------- */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-share-url]');
  if (!button) return;
  const url = button.dataset.shareUrl;
  try {
    if (navigator.share) {
      await navigator.share({ title: button.dataset.shareTitle || document.title, url });
      return;
    }
    await navigator.clipboard.writeText(url);
    Spectre.announce(button.dataset.copied);
    const label = button.textContent;
    button.textContent = button.dataset.copied;
    window.setTimeout(() => {
      button.textContent = label;
    }, 2400);
  } catch (error) {
    /* Share cancelled or clipboard blocked: nothing to do */
  }
});

/* ---------- Links to an accordion (for example /pages/faq#returns) open it ---------- */
const openTarget = () => {
  const id = decodeURIComponent(window.location.hash.slice(1));
  if (!id) return;
  const target = document.getElementById(id);
  const details = target?.tagName === 'DETAILS' ? target : target?.closest('details');
  if (details) details.open = true;
};
window.addEventListener('hashchange', openTarget);

ghost();
pauseOffscreen();
openTarget();
viewTransitions();
cardSwipe();
floatYield();
